import React, { useState, useEffect, useCallback } from "react";
import { Search, Calendar, Package, Trash2, User } from "lucide-react";
import { getOrders, updateOrder, deleteOrder } from "../lib/pocketbase";
import { Order } from "../types";
import {
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableHeaderCell,
} from "../components/ui/Table";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";

const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [savedOrders, setSavedOrders] = useState<Set<string>>(new Set());
  const [updateTimeouts, setUpdateTimeouts] = useState<
    Map<string, NodeJS.Timeout>
  >(new Map());
  const [deleteConfirmOrder, setDeleteConfirmOrder] = useState<Order | null>(
    null
  );
  const [deletingOrder, setDeletingOrder] = useState(false);

  const formatPersianDateTime = (dateString: string): string => {
    const date = new Date(dateString);

    // Convert to Persian date
    const persianDate = new Intl.DateTimeFormat("fa-IR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);

    // Convert Persian numbers to English numbers
    const persianToEnglish = (str: string) => {
      const persianNumbers = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
      const englishNumbers = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

      return str
        .split("")
        .map((char) => {
          const index = persianNumbers.indexOf(char);
          return index !== -1 ? englishNumbers[index] : char;
        })
        .join("");
    };

    return persianToEnglish(persianDate);
  };

  useEffect(() => {
    fetchOrders();
  }, [currentPage]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      updateTimeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await getOrders(currentPage, perPage);
      setOrders(response.items);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      if (error?.message?.includes("autocancelled")) {
        return;
      }
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage]);

  const handleOrderUpdate = async (
    orderId: string,
    field: "title" | "weight" | "status",
    value: string
  ) => {
    try {
      await updateOrder(orderId, { [field]: value });

      // Update the local orders state
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId ? { ...order, [field]: value } : order
        )
      );

      // Show success indicator
      setSavedOrders((prev) => new Set(prev).add(orderId));

      // Remove success indicator after 2 seconds
      setTimeout(() => {
        setSavedOrders((prev) => {
          const newSet = new Set(prev);
          newSet.delete(orderId);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const debouncedOrderUpdate = (
    orderId: string,
    field: "title" | "weight" | "status",
    value: string
  ) => {
    // Clear existing timeout for this order
    const existingTimeout = updateTimeouts.get(orderId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Update local state immediately for responsive UI
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === orderId ? { ...order, [field]: value } : order
      )
    );

    // Set new timeout for API call
    const timeout = setTimeout(() => {
      handleOrderUpdate(orderId, field, value);
      setUpdateTimeouts((prev) => {
        const newMap = new Map(prev);
        newMap.delete(orderId);
        return newMap;
      });
    }, 1000); // Wait 1 second after user stops typing

    setUpdateTimeouts((prev) => new Map(prev).set(orderId, timeout));
  };

  const handleDeleteOrder = async (orderId: string) => {
    setDeletingOrder(true);
    try {
      await deleteOrder(orderId);

      // Remove order from local state
      setOrders((prevOrders) =>
        prevOrders.filter((order) => order.id !== orderId)
      );

      // Close confirmation modal
      setDeleteConfirmOrder(null);
    } catch (error) {
      console.error("Error deleting order:", error);
    } finally {
      setDeletingOrder(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "در حال آماده سازی";
      case "done":
        return "آماده شده";
      case "delivery":
        return "تحویل";
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "done":
        return "bg-green-100 text-green-800";
      case "delivery":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const filteredOrders = orders.filter(
    (order) =>
      order.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.weight?.toString().includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">مدیریت سفارشات</h1>
          <p className="text-gray-600">مشاهده و مدیریت تمام سفارشات</p>
        </div>
      </div>

      {/* Search */}
      <div className="card p-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            جستجو
          </label>
          <div className="relative">
            <Input
              type="text"
              placeholder="جستجو در سفارشات..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-8">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              هیچ سفارشی یافت نشد
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              سفارشی با این مشخصات وجود ندارد
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableHeaderCell>اطلاعات کاربر</TableHeaderCell>
              <TableHeaderCell>عنوان</TableHeaderCell>
              <TableHeaderCell>وزن</TableHeaderCell>
              <TableHeaderCell>وضعیت</TableHeaderCell>
              <TableHeaderCell>تاریخ</TableHeaderCell>
              <TableHeaderCell>عملیات</TableHeaderCell>
            </TableHeader>
            <tbody>
              {filteredOrders.map((order) => (
                <TableRow
                  key={order.id}
                  className={
                    savedOrders.has(order.id)
                      ? "bg-green-50 border-green-200"
                      : ""
                  }
                >
                  <TableCell>
                    {order.expand?.user_id ? (
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-gray-400 ml-1" />
                          <span className="text-sm text-gray-900">
                            {order.expand.user_id.first_name || ""}{" "}
                            {order.expand.user_id.last_name || ""}
                          </span>
                        </div>
                        {order.expand.user_id.phone && (
                          <div className="text-xs text-gray-500 mr-5">
                            {order.expand.user_id.phone}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">نامشخص</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <input
                      type="text"
                      value={order.title || ""}
                      onChange={(e) =>
                        debouncedOrderUpdate(order.id, "title", e.target.value)
                      }
                      placeholder="عنوان سفارش"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </TableCell>
                  <TableCell>
                    <input
                      type="text"
                      value={order.weight || ""}
                      onChange={(e) =>
                        debouncedOrderUpdate(order.id, "weight", e.target.value)
                      }
                      placeholder="وزن"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </TableCell>
                  <TableCell>
                    <select
                      value={order.status || "pending"}
                      onChange={(e) =>
                        handleOrderUpdate(
                          order.id,
                          "status",
                          e.target.value as "pending" | "done" | "delivery"
                        )
                      }
                      className={`px-2 py-1 text-xs rounded border-0 focus:ring-1 focus:ring-primary-500 ${getStatusColor(
                        order.status || "pending"
                      )}`}
                    >
                      <option value="pending">در حال آماده سازی</option>
                      <option value="done">آماده شده</option>
                      <option value="delivery">تحویل</option>
                    </select>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 ml-1" />
                      <span className="text-sm text-gray-900">
                        {formatPersianDateTime(order.created)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => setDeleteConfirmOrder(order)}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      title="حذف سفارش"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <div className="flex space-x-2 space-x-reverse">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              قبلی
            </button>
            <span className="px-3 py-2 text-sm text-gray-700">
              صفحه {currentPage} از {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              بعدی
            </button>
          </div>
        </div>
      )}

      {/* Delete Order Confirmation Modal */}
      {deleteConfirmOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              حذف سفارش
            </h3>

            <div className="mb-4">
              <p className="text-gray-600 mb-2">
                آیا مطمئن هستید که می‌خواهید این سفارش را حذف کنید؟
              </p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium text-gray-900">
                  {deleteConfirmOrder.title || "سفارش بدون عنوان"}
                </p>
                {deleteConfirmOrder.weight && (
                  <p className="text-sm text-gray-600 mt-1">
                    وزن: {deleteConfirmOrder.weight}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 space-x-reverse">
              <Button
                onClick={() => setDeleteConfirmOrder(null)}
                variant="outline"
                disabled={deletingOrder}
              >
                انصراف
              </Button>
              <Button
                onClick={() => handleDeleteOrder(deleteConfirmOrder.id)}
                disabled={deletingOrder}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {deletingOrder ? "در حال حذف..." : "حذف سفارش"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
